import type { CSSProperties, ReactNode } from 'react';

import { OrderStatusGroup } from '../core/order-status.ts';
import type { IconName } from '../ui/icons.tsx';
import { pastelFor, RemoteImage } from '../ui/kit.tsx';

import '../styles/orders.css';

/** Общее для списка заказов, экрана заказа, оценки и уведомлений. */

/** Иконка заказа по статусу: одна и та же в списке заказов и в уведомлениях. */
export function orderRowIcon(status: string): IconName {
  if (OrderStatusGroup.cancelled.has(status)) return 'close';
  if (OrderStatusGroup.onTheWay.has(status)) return 'bike';
  if (OrderStatusGroup.finished.has(status)) return 'check';
  return 'box';
}

/** Один зелёный на все экраны заказа: красный только у отмены. */
export function orderTint(status: string): string {
  return OrderStatusGroup.cancelled.has(status) ? 'var(--danger)' : 'var(--accent-ink)';
}

const GLYPHS = {
  idCard: (
    <>
      <rect x="3" y="5" width="18" height="14" rx="2.5" />
      <circle cx="8.5" cy="11" r="2" />
      <path d="M5.8 15.8c.5-1.3 1.5-1.9 2.7-1.9s2.2.6 2.7 1.9M14 10h4M14 13.5h4" />
    </>
  ),
  undo: <path d="M9 14 4.5 9.5 9 5M4.5 9.5H14a5.5 5.5 0 0 1 0 11h-3" />,
  redelivery: (
    <>
      <path d="M4 11a8 8 0 0 1 13.7-5.2L20 8M20 4v4h-4" />
      <path d="M20 13a8 8 0 0 1-13.7 5.2L4 16M4 20v-4h4" />
    </>
  ),
} satisfies Record<string, ReactNode>;

export type GlyphName = keyof typeof GLYPHS;

/** Иконки, которых нет в общем наборе; тот же контур 24×24 в цвет текста. */
export function Glyph({ name, size = 20, weight = 2 }: { name: GlyphName; size?: number; weight?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      aria-hidden="true"
      fill="none"
      stroke="currentColor"
      strokeWidth={weight}
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      {GLYPHS[name]}
    </svg>
  );
}

/** Аватар курьера; без фото — первая буква имени на пастельном круге. */
export function CourierAvatar({ url, name, size = 40 }: { url: string | null | undefined; name: string; size?: number }) {
  return (
    <span className="courier-avatar" style={{ '--size': `${size}px`, background: pastelFor(name) } as CSSProperties}>
      {url ? <RemoteImage src={url} cover eager /> : <b>{name.slice(0, 1).toUpperCase()}</b>}
    </span>
  );
}
